import React, {useCallback, useEffect, useRef, useState} from "react";
import type {
    ColDef,
    ValueSetterParams,
    ValueFormatterParams,
    ValueParserParams,
} from "ag-grid-community";
import {round} from "lodash";
import {inclusiveBaseCents} from "./pricing";

type BooleanFieldCfg = {
    type: "boolean";
    canEdit?: boolean;
    labels?: { on: string; off: string };
    toggleOnClick?: boolean;
    onAfterChange?: (params: { newValue: any; oldValue: any; row: any; updateRow: (updates: any, silent?: boolean) => void }) => void;
};

type StringToggleCfg = {
    canEdit?: boolean;
    trueValue: string;
    falseValue: string;
    labels?: { on: string; off: string };
    toggleOnClick?: boolean;
};

type FieldCfg =
    | BooleanFieldCfg
    | { type: "enum"; options: string[]; canEdit?: boolean }
    | {
    type: "number";
    precision?: number;
    canEdit?: boolean;
    displayFactor?: number;
    format?: (displayValue: number, row?: any) => string;
    parser?: (newVal: any, oldVal: any, row?: any) => any;
    onAfterChange?: (params: { newValue: any; oldValue: any; row: any; updateRow: (updates: any, silent?: boolean) => void }) => void;
}
    | {
    type: "text";
    canEdit?: boolean;
    formatter?: (v: any) => string;
    parser?: (newVal: any, oldVal: any) => any;
    equals?: (a: any, b: any) => boolean;
    onAfterChange?: (params: { newValue: any; oldValue: any; row: any; updateRow: (updates: any, silent?: boolean) => void }) => void;
};

export function whereByIdNum(idLike: string | number) {
    const id = Number(idLike);
    if (!Number.isFinite(id)) {
        throw new Error(`Invalid numeric id: ${idLike}`);
    }
    return { id: { _eq: id } };
}

export function useGridEdit<T extends { id: number }>() {
    const [baseline, setBaseline] = useState<Record<number, T>>({});
    const [pending, setPending] = useState<Record<number, Partial<T>>>({});

    const baselineRef = useRef(baseline);
    const pendingRef = useRef(pending);
    const orderRef = useRef<number[]>([]);
    useEffect(() => {
        baselineRef.current = baseline;
    }, [baseline]);
    useEffect(() => {
        pendingRef.current = pending;
    }, [pending]);

    // keepPending rebases the baseline onto fresh server rows without discarding
    // uncommitted edits — for callers that re-run their loader while the user is
    // still editing. Discard/save paths clear `pending` themselves.
    const resetBaseline = useCallback((rows: T[], opts?: { keepPending?: boolean }) => {
        const map: Record<number, T> = {};
        rows.forEach(r => { map[r.id] = r; });
        setBaseline(map);
        if (!opts?.keepPending) setPending({});
        orderRef.current = rows.map(r => r.id);
    }, []);

    const makeEditable = useCallback(<K extends keyof T>(
        field: K,
        cfg: FieldCfg,
        setRows: React.Dispatch<React.SetStateAction<T[]>>
    ): Partial<ColDef<T>> => {
        const valueFormatter = (p: ValueFormatterParams<T>) => {
            const v = p.value;
            if (cfg.type === "boolean") {
                const onLabel = cfg.labels?.on ?? "Yes";
                const offLabel = cfg.labels?.off ?? "No";
                const toBool = (x: any) => {
                    if (typeof x === "boolean") return x;
                    const s = String(x).toLowerCase();
                    return s === "true" || s === "1" || s === "yes" || s === onLabel.toLowerCase();
                };
                return toBool(v) ? onLabel : offLabel;
            }
            if (cfg.type === "number") {
                const raw = Number(v ?? 0);
                const display = cfg.displayFactor ? raw / cfg.displayFactor : raw;
                if (cfg.format) return cfg.format(display, p.data);
                if (cfg.precision != null) return Number(display).toFixed(cfg.precision);
                return String(display);
            }
            if ((cfg as any).type === "text") {
                if ((cfg as any).formatter) return (cfg as any).formatter(v);
                return String(v ?? "").trim();
            }
            return String(v ?? "");
        };

        const valueParser = (p: ValueParserParams<T>) => {
            if (cfg.type === "boolean") {
                const on = (cfg.labels?.on ?? "Yes").toLowerCase();
                const off = (cfg.labels?.off ?? "No").toLowerCase();
                const s = String(p.newValue).toLowerCase();
                if (s === "yes" || s === "true" || s === "1" || s === on) return true;
                if (s === "no" || s === "false" || s === "0" || s === off) return false;
                return !!p.newValue;
            }
            if (cfg.type === "number") {
                if (cfg.parser) {
                    return cfg.parser(p.newValue, p.oldValue, p.data);
                }
                const cleaned = String(p.newValue ?? "").replace(/[^0-9.-]/g, "");
                const displayNum = Number(cleaned);
                if (Number.isNaN(displayNum)) return typeof p.oldValue === "number" ? p.oldValue : 0;
                return cfg.displayFactor ? Math.round(displayNum * cfg.displayFactor) : displayNum;
            }
            if (cfg.type === "text") {
                return cfg.parser ? cfg.parser(p.newValue, p.oldValue) : String(p.newValue ?? "").trim();
            }
            return p.newValue;
        };

        const valueSetter = (p: ValueSetterParams<T>) => {
            const id = p.data!.id;

            let next: any;

            switch (cfg.type) {
                case "boolean": {
                    const on = (cfg.labels?.on ?? "Yes").toLowerCase();
                    const off = (cfg.labels?.off ?? "No").toLowerCase();
                    const s = String(p.newValue).toLowerCase();
                    next =
                        s === "yes" || s === "true" || s === "1" || s === on
                            ? true
                            : s === "no" || s === "false" || s === "0" || s === off
                                ? false
                                : !!p.newValue;
                    break;
                }
                case "number": {
                    const nv =
                        typeof p.newValue === "number" ? p.newValue : Number(p.newValue);
                    const ov =
                        typeof p.oldValue === "number" ? p.oldValue : Number(p.oldValue);
                    next = Number.isNaN(nv) ? (Number.isNaN(ov) ? 0 : ov) : nv;
                    break;
                }
                case "text": {
                    const c = cfg as Extract<FieldCfg, { type: "text" }>;
                    next = c.parser
                        ? c.parser(p.newValue, p.oldValue)
                        : String(p.newValue ?? "").trim();
                    break;
                }
                case "enum":
                default:
                    next = p.newValue;
            }

            const orig = (baselineRef.current[id] as any)?.[field];
            const numberEqual = (a: any, b: any) =>
                Number.isFinite(Number(a)) && Number.isFinite(Number(b))
                    ? Number(a) === Number(b)
                    : a === b;

            const equals =
                cfg.type === "number"
                    ? numberEqual(orig, next)
                    : (cfg as any).equals
                        ? (cfg as any).equals(orig, next)
                        : orig === next;

            setRows(prev =>
                prev.map(r =>
                    r.id !== id ? r : ({ ...r, [field]: next } as T)
                )
            );

            setPending(prev => {
                const copy = { ...prev };
                if (equals) {
                    if (copy[id]) {
                        delete (copy[id] as any)[String(field)];
                        if (Object.keys(copy[id]!).length === 0) delete copy[id];
                    }
                } else {
                    copy[id] = { ...(copy[id] || {}), [field]: next };
                }
                return copy;
            });

            if ((cfg as any).onAfterChange) {
                const updateRow = (updates: Partial<T>, silent = false) => {
                    setRows(prev => prev.map(r => r.id === id ? { ...r, ...updates } as T : r));

                    if (!silent) {
                        setPending(prev => ({
                            ...prev,
                            [id]: { ...(prev[id] || {}), ...updates }
                        }));
                    }
                };
                (cfg as any).onAfterChange({
                    newValue: next,
                    oldValue: p.oldValue,
                    row: p.data,
                    updateRow
                });
            }

            return true;
        };

        const toggling = cfg.type === "boolean" && cfg.toggleOnClick && cfg.canEdit !== false;
        const toggleCell = toggling
            ? (e: any) => {
                const row = e?.data as T | undefined;
                if (!row) return;
                const id = row.id;
                const curr = (row as any)[field];
                const next = !Boolean(curr);

                setRows(prev => prev.map(r => (r.id === id ? ({...r, [field]: next} as T) : r)));

                const orig = (baselineRef.current[id] as any)?.[field];
                setPending(prev => {
                    const copy: Record<number, Partial<T>> = {...prev};
                    if (orig === next) {
                        if (copy[id]) {
                            delete (copy[id] as any)[String(field)];
                            if (Object.keys(copy[id]!).length === 0) delete copy[id];
                        }
                    } else {
                        copy[id] = {...(copy[id] || {}), [field]: next};
                    }
                    return copy;
                });

                if (cfg.type === "boolean" && cfg.onAfterChange) {
                    const updateRow = (updates: Partial<T>, silent = false) => {
                        setRows(prev => prev.map(r => r.id === id ? { ...r, ...updates } as T : r));

                        if (!silent) {
                            setPending(prev => ({
                                ...prev,
                                [id]: { ...(prev[id] || {}), ...updates }
                            }));
                        }
                    };
                    cfg.onAfterChange({
                        newValue: next,
                        oldValue: curr,
                        row,
                        updateRow
                    });
                }
            }
            : undefined;

        const editorBits =
            cfg.type === "boolean" && !cfg.toggleOnClick
                ? {
                    cellEditor: "agSelectCellEditor",
                    cellEditorParams: {values: [cfg.labels?.on ?? "Yes", cfg.labels?.off ?? "No"]}
                }
                : cfg.type === "enum"
                    ? {cellEditor: "agSelectCellEditor", cellEditorParams: {values: cfg.options}}
                    : cfg.type === "number"
                        ? {cellEditor: "agTextCellEditor", cellEditorParams: {useFormatter: true}}
                        : {};

        return {
            editable: toggling ? false : cfg.canEdit !== false,
            valueFormatter,
            valueParser,
            valueSetter,
            cellClassRules: {
                "cell-dirty": (p) => {
                    const id = p.data?.id as number | undefined;
                    if (!id) return false;
                    const patch = pendingRef.current[id];
                    if (!patch) return false;
                    return Object.prototype.hasOwnProperty.call(patch, String(field));
                },
            },
            onCellClicked: toggling ? toggleCell : undefined,
            suppressKeyboardEvent: toggling
                ? (params) => {
                    const key = (params.event as KeyboardEvent | undefined)?.key;
                    if (key === "Enter" || key === " ") {
                        toggleCell?.({data: params.node?.data});
                        return true;
                    }
                    return false;
                }
                : undefined,
            cellStyle: toggling ? {cursor: "pointer"} : undefined,
            ...editorBits,
        };
    }, []);

    const makeStringToggle = useCallback(
        function <K extends keyof T>(
            field: K,
            cfg: StringToggleCfg,
            setRows: React.Dispatch<React.SetStateAction<T[]>>
        ): Partial<ColDef<T>> {
            const onLabel = cfg.labels?.on ?? "On";
            const offLabel = cfg.labels?.off ?? "Off";
            const TRUE = String(cfg.trueValue).toLowerCase().trim();
            const FALSE = String(cfg.falseValue).toLowerCase().trim();

            const normalize = (v: any): "true" | "false" => {
                const s = String(v ?? "").toLowerCase().trim();
                if (s === TRUE) return "true";
                if (s === FALSE) return "false";
                if (["true", "1", "yes", "inclusive"].includes(s)) {
                    return TRUE === "inclusive" ? "true" : "false";
                }
                return FALSE === "exclusive" ? "false" : "true";
            };

            const base = makeEditable(
                field,
                {
                    type: "text",
                    canEdit: cfg.canEdit !== false,
                    parser: (nv: any, ov: any) =>
                        normalize(nv) === "true" ? cfg.trueValue : cfg.falseValue,
                    formatter: (v: any) => (normalize(v) === "true" ? onLabel : offLabel),
                    equals: (a: any, b: any) => normalize(a) === normalize(b),
                },
                setRows
            );

            const toggling = (cfg.toggleOnClick ?? true) && cfg.canEdit !== false;

            const toggleCell = toggling
                ? (e: any) => {
                    const curr = normalize(e?.value ?? (e?.data as any)?.[field]);
                    const next = curr === "true" ? cfg.falseValue : cfg.trueValue;
                    e.node.setDataValue(String(field), next);
                }
                : undefined;

            return {
                ...base,
                editable: toggling ? false : cfg.canEdit !== false,
                valueFormatter: (p) =>
                    normalize(p.value) === "true" ? onLabel : offLabel,
                onCellClicked: toggleCell,
                suppressKeyboardEvent: toggling
                    ? (params) => {
                        const key = (params.event as KeyboardEvent | undefined)?.key;
                        if (key === "Enter" || key === " ") {
                            toggleCell?.({
                                value: (params as any).value,
                                data: params.node?.data,
                                node: params.node,
                            });
                            return true;
                        }
                        return false;
                    }
                    : undefined,
                cellStyle: toggling ? { cursor: "pointer" } : undefined,
            };
        },
        [makeEditable]
    );

    const getBaselineRows = useCallback((): T[] => {
        const base = baselineRef.current;
        const order = orderRef.current;
        if (order.length) return order.map(id => base[id]).filter(Boolean);
        return Object.values(base);
    }, []);

    return {
        pending,
        pendingCount: Object.keys(pending).length,
        resetBaseline,
        makeEditable,
        makeStringToggle,
        setPending,
        getBaselineRows
    };
}

// RP-1955: an inclusive item shows its stored menu_price (what the customer pays); items saved before
// the column existed fall back to base plus rounded tax.
export const calculateMenuPriceDisplay = (
    storedPriceCents: number,
    taxPercentage: number,
    isExclusive: boolean,
    menuPriceCents?: number | null
): number => {
    if (!isExclusive) {
        const stored = Number(menuPriceCents);
        if (Number.isFinite(stored) && stored > 0) {
            return +(Math.round(stored) / 100).toFixed(2);
        }
        const baseCents = Math.round(storedPriceCents);
        const finalCents = Math.round(baseCents * (1 + taxPercentage));
        return +(finalCents / 100).toFixed(2);
    }

    return +(storedPriceCents / 100).toFixed(2);
};

export const calculatePriceWithTax = (
    storedPriceCents: number,
    taxPercentage: number,
    isExclusive: boolean,
    menuPriceCents?: number | null
): number => {
    if (isExclusive) {
        const finalCents = Math.round(storedPriceCents * (1 + taxPercentage));
        return +(finalCents / 100).toFixed(2);
    } else {
        return calculateMenuPriceDisplay(storedPriceCents, taxPercentage, isExclusive, menuPriceCents);
    }
};

// RP-1955: the base of an inclusive item is the floor of menu / (1 + rate); the remainder is tax.
export const calculateStoredPriceFromMenu = (
    menuPriceDollars: number,
    taxPercentage: number,
    isExclusive: boolean
): number => {
    const menuCents = Math.round(menuPriceDollars * 100);
    if (isExclusive) {
        return menuCents;
    }
    return inclusiveBaseCents(menuCents, taxPercentage);
};
